import { cn } from "@lib/cn";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { usePointerDrag } from "../hooks/use-pointer-drag";
import { isCssVar, resolveCssVar } from "../lib/rgba";
import type { GradientStop } from "../lib/types";

export type StopHandleProps = {
	stop: GradientStop;
	index: number;
	isSelected: boolean;
	canRemove: boolean;
	onSelect: (id: string) => void;
	onPositionChange: (id: string, position: number) => void;
	onDelete: (id: string) => void;
};

/** Figma shadow-sm */
const HANDLE_SHADOW =
	"0px 8px 2px rgba(10,10,10,0), 0px 5px 2px rgba(10,10,10,0.01), 0px 3px 2px rgba(10,10,10,0.03), 0px 1px 1px rgba(10,10,10,0.04), 0px 0px 1px rgba(10,10,10,0.05)";

const clamp = (n: number): number => Math.max(0, Math.min(100, n));

/** Circular draggable gradient stop handle — 24×24px, Figma spec. */
export const StopHandle = ({
	stop,
	index,
	isSelected,
	canRemove,
	onSelect,
	onPositionChange,
	onDelete,
}: StopHandleProps) => {
	const { onPointerDown } = usePointerDrag({
		onStart: () => {
			onSelect(stop.id);
			return true;
		},
		onMove: () => {},
	});

	const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
		const { key, shiftKey } = e;
		const bigStep = shiftKey ? 10 : 1;
		switch (key) {
			case "ArrowRight":
			case "ArrowUp":
				e.preventDefault();
				onPositionChange(stop.id, clamp(stop.position + bigStep));
				return;
			case "ArrowLeft":
			case "ArrowDown":
				e.preventDefault();
				onPositionChange(stop.id, clamp(stop.position - bigStep));
				return;
			case "PageUp":
				e.preventDefault();
				onPositionChange(stop.id, clamp(stop.position + 10));
				return;
			case "PageDown":
				e.preventDefault();
				onPositionChange(stop.id, clamp(stop.position - 10));
				return;
			case "Home":
				e.preventDefault();
				onPositionChange(stop.id, 0);
				return;
			case "End":
				e.preventDefault();
				onPositionChange(stop.id, 100);
				return;
			case "Delete":
			case "Backspace":
				e.preventDefault();
				if (canRemove) onDelete(stop.id);
				return;
		}
	};

	const resolvedColor = isCssVar(stop.color)
		? resolveCssVar(stop.color)
		: stop.color;

	return (
		<div
			role="slider"
			tabIndex={0}
			aria-label={`Gradient stop ${index + 1}`}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={stop.position}
			aria-valuetext={`${stop.position} percent`}
			aria-orientation="horizontal"
			data-testid="stop-handle"
			data-selected={isSelected ? "true" : "false"}
			style={{
				touchAction: "none",
				boxShadow: `${HANDLE_SHADOW}, 0 0 0 1px rgba(18, 3, 62, 0.1)`,
				backgroundColor: resolvedColor,
			}}
			onPointerDown={onPointerDown}
			onKeyDown={handleKeyDown}
			className={cn(
				"tw-size-6 tw-cursor-pointer tw-rounded-full tw-border-2 tw-border-solid tw-border-white tw-outline-none",
				isSelected && "tw-ring-2 tw-ring-primary-500",
			)}
		/>
	);
};
